import { useCallback, Component, type ReactNode } from 'react';
import { Box, Typography, Button, useMediaQuery } from '@mui/material';
import { HeaderBar } from './HeaderBar';
import { TabBar } from './TabBar';
import { DetailPanel } from './DetailPanel';
import { WorklistPanel } from '../worklist/WorklistPanel';
import { useUIStore, type DynamicTab } from '../../stores/uiStore';
import { useResizableSplitter } from '../../hooks/useResizableSplitter';
import { useHotKeys } from '../hotkeys/useHotKeys';
import { useUpdates } from '../../hooks/useUpdates';
import { useStatePersistence } from '../../hooks/useStatePersistence';
import { AdvancedSearchDialog } from '../search/AdvancedSearchDialog';
import { SavedSearchDialog } from '../search/SavedSearchDialog';
import { UserPreferencesDialog } from '../preferences/UserPreferencesDialog';
import { StudyActionDialogs } from '../worklist/StudyActionDialogs';
import { RoentgenDrawer } from '../roentgen/RoentgenDrawer';

// Admin panels
import { UsersPanel, SitesPanel, HL7QueuePanel, JobsPanel, FaxQueuePanel, RulesPanel, SystemSummaryPanel, LogViewerPanel, NotificationsPanel, SystemLabelsPanel, FireDrillPanel } from '../admin';
// Other panels
import { MessagingPanel } from '../messaging/MessagingPanel';
import { SchedulingPanel } from '../scheduling/SchedulingPanel';
import { PatientPanel } from '../patients/PatientPanel';
import { ReportViewer } from '../reports/ReportViewer';
import { ViewerTab } from '../viewer/ViewerTab';
import { ViewerContextProvider } from '../viewer/ViewerContext';
import { getViewer, getDefaultViewer } from '../viewer/viewerRegistry';
import { ProtocolsPanel } from '../viewer/protocols/ProtocolsPanel';

/** Worklist tab IDs — these render WorklistPanel */
const WORKLIST_TABS = new Set([
  // Default status tabs
  'studies', 'newstudy', 'assigned', 'dictated', 'read',
  'urgent', 'onhold', 'unassigned', 'inprogress', 'delivered',
  'transcribed', 'ordered', 'scheduled',
  // Global / admin study views
  'actionneeded', 'discordance', 'peerreview', 'queued', 'sent',
  'hasquiz', 'qcall', 'unsigned', 'signed',
  // "My" radiologist tabs
  'myactionneeded', 'myqueued', 'mydelivered', 'myread',
  'mydictated', 'myunsigned', 'mysigned',
  // Physician tab
  'mypatients',
  // Resident tabs
  'myqueued_resident', 'myreported_resident', 'myscored_resident',
  // Attending → Resident tabs
  'myresident_assigned', 'myresident_reported', 'myresident_scored',
  // Additional views
  'myunread', 'unread', 'today', 'lastweek', 'accessed',
  'has_bi_rads', 'shared_by_me', 'shared_with_me', 'ssl_late',
  // Special
  'mystudies', 'url',
]);

/** Map admin panel names to components */
const ADMIN_PANELS: Record<string, React.ComponentType> = {
  users: UsersPanel,
  sites: SitesPanel,
  hl7queue: HL7QueuePanel,
  jobs: JobsPanel,
  faxqueue: FaxQueuePanel,
  rules: RulesPanel,
  systemsummary: SystemSummaryPanel,
  logviewer: LogViewerPanel,
  notifications: NotificationsPanel,
  protocols: ProtocolsPanel,
  systemlabels: SystemLabelsPanel,
  firedrill: FireDrillPanel,
};

/**
 * Error boundary that catches viewer tab crashes (e.g. OOM on large studies).
 * Shows a recovery message and lets the user close the tab.
 */
class ViewerErrorBoundary extends Component<
  { children: ReactNode; onClose: () => void },
  { hasError: boolean; errorMsg: string }
> {
  constructor(props: { children: ReactNode; onClose: () => void }) {
    super(props);
    this.state = { hasError: false, errorMsg: '' };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMsg: error.message || 'Unknown error' };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" color="error">
            Viewer crashed
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {this.state.errorMsg}
          </Typography>
          <Button variant="contained" onClick={this.props.onClose}>
            Close Tab
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

/**
 * GWT-matching app shell — mirrors MainView.java layout.
 *
 * Layout (normal mode):
 *   ┌───────────────────────────────────────────┐
 *   │ HeaderBar (36px, or 72px if < 1500px)     │
 *   ├───────────────────────────────────────────┤
 *   │ TabBar (32px)                             │
 *   ├───────────────────────────────────────────┤
 *   │ Content area (fills remaining height)     │
 *   │                                           │
 *   ├───────────────────────────────────────────┤ ← drag splitter
 *   │ DetailPanel (resizable, south)            │
 *   └───────────────────────────────────────────┘
 *
 * Layout (split-screen mode):
 *   ┌───────────────────────────────────────────┐
 *   │ HeaderBar                                 │
 *   ├───────────────────────────────────────────┤
 *   │ TabBar                                    │
 *   ├─────────────────┬─┬──────────────────────┤
 *   │ Worklist         │▌│ Viewer (pinned)      │
 *   │                  │▌│                      │
 *   │                  │▌│                      │
 *   ├─────────────────┴─┴──────────────────────┤
 *   │ DetailPanel (south)                       │
 *   └───────────────────────────────────────────┘
 */
export const AppShell = () => {
  const isNarrow = useMediaQuery('(max-width:1499px)');
  const headerHeight = isNarrow ? 72 : 36;
  const tabBarHeight = 32;

  const activeTab = useUIStore((s) => s.activeTab);
  const detailPanelOpen = useUIStore((s) => s.detailPanelOpen);
  const dynamicTabs = useUIStore((s) => s.dynamicTabs);
  const advancedSearchOpen = useUIStore((s) => s.advancedSearchOpen);
  const preferencesOpen = useUIStore((s) => s.preferencesOpen);
  const savedSearchOpen = useUIStore((s) => s.savedSearchOpen);
  const setAdvancedSearchOpen = useUIStore((s) => s.setAdvancedSearchOpen);
  const setPreferencesOpen = useUIStore((s) => s.setPreferencesOpen);
  const setSavedSearchOpen = useUIStore((s) => s.setSavedSearchOpen);
  const closeDynamicTab = useUIStore((s) => s.closeDynamicTab);
  const splitScreen = useUIStore((s) => s.splitScreen);
  const pinnedViewerTabId = useUIStore((s) => s.pinnedViewerTabId);

  // Global keyboard shortcuts
  useHotKeys();

  // Real-time long-poll updates
  useUpdates();

  // Persist and restore UI state across sessions
  useStatePersistence();

  const {
    size: detailHeight,
    splitterProps,
  } = useResizableSplitter({
    direction: 'vertical',
    initialSize: 220,
    minSize: 120,
    maxSize: 500,
  });

  // Horizontal splitter for split-screen mode (worklist width)
  const {
    size: splitWorklistWidth,
    splitterProps: hSplitterProps,
  } = useResizableSplitter({
    direction: 'horizontal',
    initialSize: 520,
    minSize: 320,
    maxSize: 900,
  });

  // Resolve pinned viewer tab object
  const pinnedTab = splitScreen
    ? dynamicTabs.find((t) => t.id === pinnedViewerTabId && t.type === 'viewer')
    : undefined;
  // Split is active when enabled AND we have a valid pinned viewer
  const splitActive = splitScreen && !!pinnedTab;

  /** Resolve which panel to render for the active tab */
  const renderContent = useCallback(() => {
    // Worklist tabs
    if (WORKLIST_TABS.has(activeTab)) {
      return <WorklistPanel />;
    }

    // Find dynamic tab
    const dTab = dynamicTabs.find((t) => t.id === activeTab);
    if (!dTab) {
      // Fallback — treat as worklist if unknown
      return <WorklistPanel />;
    }

    return <DynamicTabContent tab={dTab} onClose={() => closeDynamicTab(dTab.id)} />;
  }, [activeTab, dynamicTabs, closeDynamicTab]);

  // Only show detail panel for worklist tabs
  const showDetailPanel = detailPanelOpen && WORKLIST_TABS.has(activeTab);

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: 'background.default',
      }}
    >
      {/* Header — topPanel */}
      <Box sx={{ height: headerHeight, flexShrink: 0 }}>
        <HeaderBar height={headerHeight} isNarrow={isNarrow} />
      </Box>

      {/* Tab bar — CustomTabLayoutPanel tab strip */}
      <Box sx={{ height: tabBarHeight, flexShrink: 0 }}>
        <TabBar />
      </Box>

      {/* Content + Detail split */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Main content area — either single pane or split */}
        {splitActive ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
            {/* Left pane — worklist (always shown in split mode) */}
            <Box sx={{ width: splitWorklistWidth, flexShrink: 0, overflow: 'hidden' }}>
              <WorklistPanel />
            </Box>

            {/* Vertical resize splitter */}
            <Box
              {...hSplitterProps}
              sx={{
                width: 5,
                flexShrink: 0,
                cursor: 'col-resize',
                backgroundColor: 'divider',
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
              }}
            />

            {/* Right pane — pinned viewer */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <DynamicTabContent
                tab={pinnedTab}
                onClose={() => closeDynamicTab(pinnedTab.id)}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {renderContent()}
          </Box>
        )}

        {/* Drag splitter */}
        {showDetailPanel && (
          <>
            <Box
              {...splitterProps}
              sx={{
                height: 5,
                flexShrink: 0,
                cursor: 'row-resize',
                backgroundColor: 'divider',
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
              }}
            />
            {/* Detail panel — south */}
            <Box sx={{ height: detailHeight, flexShrink: 0, overflow: 'auto' }}>
              <DetailPanel />
            </Box>
          </>
        )}
      </Box>

      {/* Global dialogs */}
      <AdvancedSearchDialog
        open={advancedSearchOpen}
        onClose={() => setAdvancedSearchOpen(false)}
      />
      <SavedSearchDialog
        open={savedSearchOpen}
        onClose={() => setSavedSearchOpen(false)}
      />
      <UserPreferencesDialog
        open={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />
      <StudyActionDialogs />
      <RoentgenDrawer />
    </Box>
  );
};

/**
 * Renders the correct panel for a dynamic tab.
 */
const DynamicTabContent = ({ tab, onClose }: { tab: DynamicTab; onClose: () => void }) => {
  const selectedStudies = useUIStore((s) => s.selectedStudies);

  switch (tab.type) {
    case 'admin': {
      const Panel = ADMIN_PANELS[tab.adminPanel ?? ''];
      return Panel ? <Panel /> : <WorklistPanel />;
    }
    case 'viewer':
      return tab.study ? (
        <ViewerErrorBoundary onClose={onClose}>
          <ViewerContextProvider value={getViewer(tab.viewerId ?? '') ?? getDefaultViewer()}>
            <ViewerTab study={tab.study} onClose={onClose} />
          </ViewerContextProvider>
        </ViewerErrorBoundary>
      ) : <WorklistPanel />;
    case 'messaging':
      return <MessagingPanel />;
    case 'scheduling':
      return <SchedulingPanel />;
    case 'patients':
      return <PatientPanel />;
    case 'reports':
      return <ReportViewer study={selectedStudies[selectedStudies.length - 1] ?? null} />;
    default:
      return <WorklistPanel />;
  }
};
